import { ZIP_REGEX } from '@constants/app.constants';
import { StoreDbModel } from '@models/store-db.model';

// Same normalisation as the personal form's zip blur handler (PersonalFormComponent.onZipBlur()).
// Every comparison against the store-search postcode goes through this, so a zip the blur handler
// rewrote still counts as the postcode it was prefilled with.
export function normalizePostcode(value: unknown): string {
  return String(value ?? '').trim().toUpperCase().replace(/\s+/g, ' ');
}

/** A term that is a postcode and nothing else: 3 to 10 chars of letters/digits/space/hyphen, at least one
 *  digit, and no run of three or more letters (so "3011 Bern", "6300 Zug", "Hofweg 12" are rejected while
 *  "1003", "80331", "1234 AB", "12-345", "123 45", "SW1A 1AA" are accepted). */
export function isPlainPostcode(term: string | null | undefined): boolean {
  const t = (term ?? '').trim();
  return /^(?=.*\d)[A-Za-z0-9][A-Za-z0-9 -]{2,9}$/.test(t) && !/[A-Za-z]{3,}/.test(t);
}

/**
 * The postcode the customer entered in the store search, as the canonical value of the store row
 * they picked, or null when it cannot be told apart from a city / canton / store-name term.
 *
 * The postcode search returns one row per matching postcode, so a single store can appear several
 * times with different postcodes. The dropdown binds `store.id`, so when those rows disagree the
 * row the customer actually picked cannot be recovered, and nothing is returned.
 *
 * Without `postcode_details` (city names not included in the results, or "any store" = id 0) the
 * typed term itself is the candidate, but only when it is a plain postcode (isPlainPostcode): the
 * search also takes free text such as "3011 Bern", which the zip input's own pattern would accept.
 * A `postcode_details.postcode` value is canonical from the backend and is used as it is.
 *
 * A picked store that is not in the current results (the customer searched again after picking)
 * gives null: the term belongs to the new search, not to that store. "Any store" (id 0) never has
 * a row of its own and keeps the typed-term fallback.
 */
export function resolveCustomerEnteredPostcode(
  term: string | null | undefined,
  stores: StoreDbModel[] | null | undefined,
  selectedStoreId: number | null | undefined,
): string | null {
  const results = stores ?? [];
  // A real store must be in the snapshot the pick was made from; an empty snapshot (a search that replaced the
  // results after the pick, or none at all) can never vouch for the term.
  if (selectedStoreId !== 0 && !results.some(store => store?.id === selectedStoreId)) {
    return null;
  }
  const rows = results.filter(store => store?.id === selectedStoreId && !!store?.postcode_details?.postcode);
  const typed = normalizePostcode(term);

  const typedMatch = typed ? rows.find(row => normalizePostcode(row.postcode_details.postcode) === typed) : undefined;
  if (typedMatch) {
    return acceptedByZipField(typedMatch.postcode_details.postcode);
  }
  if (rows.length > 0) {
    const distinct = new Set(rows.map(row => normalizePostcode(row.postcode_details.postcode)));
    return distinct.size === 1 ? acceptedByZipField(rows[0].postcode_details.postcode) : null;
  }
  return isPlainPostcode(term) ? term.trim() : null;
}

/** Never hand back a value the zip field's own validator would reject: an invalid prefill would block the form. */
function acceptedByZipField(postcode: string): string | null {
  return ZIP_REGEX.test(postcode) ? postcode : null;
}

/** True when the zip field still holds the store-search postcode. An empty field never counts. */
export function isZipPrefilledFromStorePostcode(zip: unknown, storePostcode: unknown): boolean {
  const normalizedZip = normalizePostcode(zip);
  const normalizedPostcode = normalizePostcode(storePostcode);
  return normalizedZip !== '' && normalizedPostcode !== '' && normalizedZip === normalizedPostcode;
}

export interface PersonalFormZipResolution {
  zip: string | undefined;
  filledFromPostcode: string | null;
}

/**
 * The single precedence rule for the personal form's zip. `filledFromPostcode` is the normalised
 * store postcode this widget last wrote into the zip (persisted in the appointment state), so only
 * a value the widget filled itself is ever replaced or removed.
 *  1. `explicitCustomerZip` (customer_prefill.zip) set: the zip is left alone and nothing is marked;
 *     the form's own prefill fills it later.
 *  2. With a store-search postcode: an empty zip, or the zip the widget filled, takes it; any other
 *     zip is the customer's own and is kept.
 *  3. Without one: the zip the widget filled is removed (its search is gone); anything else is kept.
 * Only a zip the widget has just filled carries a marker. A marker that no longer matches the zip
 * (the customer changed it, or the fill was lost) is cleared, so a later identical value the
 * customer typed is never mistaken for the widget's own.
 */
export function resolvePersonalFormZip(args: {
  currentZip: string | null | undefined;
  explicitCustomerZip: string | null | undefined;
  storePostcode: string | null;
  filledFromPostcode: string | null;
}): PersonalFormZipResolution {
  const { currentZip, explicitCustomerZip, storePostcode, filledFromPostcode } = args;
  const zip = currentZip ?? undefined;
  if (normalizePostcode(explicitCustomerZip) !== '') {
    return { zip, filledFromPostcode: null };
  }

  const normalizedZip = normalizePostcode(zip);
  const filledByWidget = normalizedZip !== '' && normalizedZip === filledFromPostcode;
  if (normalizePostcode(storePostcode) !== '') {
    if (normalizedZip === '' || filledByWidget) {
      return { zip: storePostcode, filledFromPostcode: normalizePostcode(storePostcode) };
    }
    return { zip, filledFromPostcode: null };
  }

  if (filledByWidget) {
    return { zip: undefined, filledFromPostcode: null };
  }
  return { zip, filledFromPostcode: null };
}
